import React, { useCallback, useEffect, useRef, useState } from "react";
import { api, arabicError } from "../lib/api";
import VoiceRecorder from "../components/VoiceRecorder";
import GmailConnect from "../components/GmailConnect";
import { toast } from "sonner";
import { Globe, Send, Plus, MessageCircle, ChevronRight, Bot, Trash2 } from "lucide-react";
import { Link, useSearchParams } from "react-router-dom";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogFooter, AlertDialogTitle, AlertDialogDescription,
  AlertDialogAction, AlertDialogCancel,
} from "../components/ui/alert-dialog";

const fmt = (ts) => (ts ? new Date(ts).toLocaleString("ar-SY", { dateStyle: "short", timeStyle: "short" }) : "");

const ExternalSourcing = () => {
  const [convs, setConvs] = useState([]);
  const [active, setActive] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [showList, setShowList] = useState(false);
  const [toDelete, setToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const endRef = useRef(null);
  const [searchParams] = useSearchParams();
  const wantedConv = searchParams.get("c");

  const loadConvs = useCallback(async () => {
    try {
      const r = await api.get("/ai/sourcing/conversations");
      setConvs(r.data);
      return r.data;
    } catch (e) { toast.error(arabicError(e)); return []; }
  }, []);

  const openConv = useCallback(async (c) => {
    setActive(c);
    setShowList(false);
    try { setMessages((await api.get(`/ai/sourcing/conversations/${c.id}/messages`)).data); }
    catch (e) { toast.error(arabicError(e)); }
  }, []);

  const newConv = async () => {
    try {
      const r = await api.post("/ai/sourcing/conversations");
      setConvs((prev) => [r.data, ...prev]);
      setActive(r.data); setMessages([]); setShowList(false);
    } catch (e) { toast.error(arabicError(e)); }
  };

  // Deletes ONLY after explicit confirmation in the AlertDialog; IMDAD records only (Gmail is never touched).
  const confirmDelete = async () => {
    if (!toDelete) return;
    setDeleting(true);
    try {
      await api.delete(`/ai/sourcing/conversations/${toDelete.id}`);
      const rest = convs.filter((c) => c.id !== toDelete.id);
      setConvs(rest);
      if (active?.id === toDelete.id) {
        if (rest[0]) await openConv(rest[0]); else { setActive(null); setMessages([]); }
      }
      toast.success("تم حذف المحادثة");
    } catch (e) { toast.error(arabicError(e)); }
    finally { setDeleting(false); setToDelete(null); }
  };

  useEffect(() => {
    (async () => {
      const list = await loadConvs();
      const target = (wantedConv && list.find((c) => c.id === wantedConv)) || list[0];
      if (target) await openConv(target);
      setLoading(false);
    })();
  }, [loadConvs, openConv, wantedConv]);

  // Supplier replies are appended server-side to the SAME conversation → refresh when one arrives for the open one
  useEffect(() => {
    const onReply = (e) => {
      const cid = e.detail?.conversation_id;
      if (cid && active?.id === cid) openConv(active);
      loadConvs();
    };
    window.addEventListener("imdad:supplier_reply", onReply);
    return () => window.removeEventListener("imdad:supplier_reply", onReply);
  }, [active, openConv, loadConvs]);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, sending]);

  const send = async (e) => {
    e?.preventDefault();
    const text = input.trim();
    if (!text || sending) return;
    let conv = active;
    setSending(true);
    try {
      if (!conv) {
        const r = await api.post("/ai/sourcing/conversations");
        conv = r.data; setActive(conv); setConvs((prev) => [conv, ...prev]);
      }
      setInput("");
      setMessages((prev) => [...prev, { id: `tmp-${Date.now()}`, role: "user", content: text, ts: new Date().toISOString() }]);
      const r = await api.post(`/ai/sourcing/conversations/${conv.id}/messages`, { text });
      setMessages((prev) => [...prev.filter((m) => !String(m.id).startsWith("tmp-")), r.data.user, r.data.assistant]);
      loadConvs();
    } catch (err) {
      toast.error(arabicError(err));
      setMessages((prev) => prev.filter((m) => !String(m.id).startsWith("tmp-")));
      setInput(text);
    } finally { setSending(false); }
  };

  return (
    <div className="space-y-4" data-testid="external-sourcing-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <Link to="/messages" className="text-xs text-[#088395] font-bold flex items-center gap-1 hover:underline" data-testid="sourcing-back-to-messages">
            <ChevronRight size={14} /> الرسائل
          </Link>
          <h1 className="text-3xl md:text-4xl font-extrabold text-[#333333] flex items-center gap-2 mt-1"><Globe className="text-[#09637E]" size={30} /> التوريد الخارجي</h1>
          <p className="text-[#666666] mt-1 text-sm md:text-base">وكيل ذكي يساعدك في البحث عن المصانع والموردين ومقارنة العروض</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setShowList((v) => !v)} data-testid="sourcing-toggle-list"
            className="h-10 px-3 rounded-lg border border-[#7AB2B2] text-[#09637E] text-sm font-bold flex items-center gap-1 hover:bg-[#EBF4F6]">
            <MessageCircle size={16} /> محادثاتي ({convs.length})
          </button>
          <button onClick={newConv} data-testid="sourcing-new-conversation"
            className="h-10 px-3 rounded-lg bg-[#09637E] hover:bg-[#088395] text-white text-sm font-bold flex items-center gap-1">
            <Plus size={16} /> محادثة جديدة
          </button>
        </div>
      </div>
      <GmailConnect />

      {showList && (
        <div className="bg-white border border-[#7AB2B2] rounded-2xl divide-y divide-[#EBF4F6] max-h-64 overflow-y-auto" data-testid="sourcing-conversations-list">
          {convs.length === 0 ? <div className="p-4 text-center text-[#666666] text-sm">لا توجد محادثات بعد</div>
            : convs.map((c) => (
              <div key={c.id} className={`flex items-stretch ${active?.id === c.id ? "bg-[#EBF4F6]" : ""}`}>
                <button onClick={() => openConv(c)} data-testid={`sourcing-conv-${c.id}`}
                  className="flex-1 min-w-0 text-right p-3 hover:bg-[#EBF4F6]">
                  <div className="font-bold text-[#333333] truncate text-sm">{c.title}</div>
                  {c.kind === "supplier_inbound" && <span className="inline-block mt-0.5 text-[10px] font-bold px-2 py-0.5 rounded-full bg-[#09637E]/10 text-[#09637E]" data-testid={`sourcing-inbound-badge-${c.id}`}>بريد جديد من مورّد</span>}
                  <div className="text-[11px] text-[#666666]">{fmt(c.updated_at)} · {c.message_count || 0} رسالة</div>
                </button>
                <button type="button" onClick={(e) => { e.stopPropagation(); setToDelete(c); }} aria-label="حذف المحادثة" title="حذف المحادثة"
                  data-testid={`sourcing-conv-delete-${c.id}`}
                  className="px-3 text-[#666666] hover:text-red-600 hover:bg-red-50 transition-colors">
                  <Trash2 size={16} />
                </button>
              </div>
            ))}
        </div>
      )}

      <AlertDialog open={!!toDelete} onOpenChange={(o) => { if (!o && !deleting) setToDelete(null); }}>
        <AlertDialogContent dir="rtl" data-testid="sourcing-delete-dialog">
          <AlertDialogHeader className="text-right">
            <AlertDialogTitle>هل أنت متأكد من حذف هذه المحادثة؟</AlertDialogTitle>
            <AlertDialogDescription>
              «{toDelete?.title}» — سيُحذف سجل هذه المحادثة ومسوداتها وملخصات ردود الموردين فيها من إمداد. لن تُحذف أي رسائل من بريدك في Gmail ولن يتأثر ربط Gmail.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-2 sm:justify-start">
            <AlertDialogCancel disabled={deleting} data-testid="sourcing-delete-cancel">إلغاء</AlertDialogCancel>
            <AlertDialogAction onClick={(e) => { e.preventDefault(); confirmDelete(); }} disabled={deleting}
              className="bg-red-600 hover:bg-red-700 text-white" data-testid="sourcing-delete-confirm">
              {deleting ? "جارٍ الحذف…" : "نعم، حذف المحادثة"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <div className="bg-white border border-[#7AB2B2] rounded-2xl flex flex-col h-[68vh]">
        <div className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="sourcing-messages">
          {loading ? <div className="text-center text-[#666666] py-8">جارٍ التحميل...</div>
            : messages.length === 0 ? (
              <div className="text-center text-[#666666] py-10 space-y-2" data-testid="sourcing-empty">
                <Bot className="mx-auto text-[#7AB2B2]" size={40} />
                <p className="font-bold">ابدأ بوصف ما تبحث عنه</p>
                <p className="text-sm">مثال: «شفلي معامل سيراميك بالسعودية» أو «قارن لي بين موردي البورسلان في تركيا»</p>
              </div>
            ) : messages.map((m) => {
              const mine = m.role === "user";
              return (
                <div key={m.id} className={`flex ${mine ? "justify-start" : "justify-end"}`} data-testid={`sourcing-msg-${m.role}-${m.id}`}>
                  <div className={`max-w-[85%] md:max-w-[75%] rounded-2xl p-3 ${mine ? "bg-[#09637E] text-white" : "bg-[#EBF4F6] text-[#333333]"}`}>
                    <div className="text-xs font-bold opacity-80 mb-1">{mine ? "أنت" : m.kind === "supplier_reply" ? (m.supplier?.independent ? "وكيل التوريد · بريد جديد من مورّد" : "وكيل التوريد · رد مورّد") : "وكيل التوريد"}</div>
                    <div className="whitespace-pre-wrap break-words leading-relaxed text-sm md:text-base">{m.content}</div>
                    {!mine && m.sent_emails?.length > 0 && (
                      <div className="mt-2 pt-2 border-t border-[#7AB2B2]/40 text-xs space-y-1" data-testid={`sourcing-sent-${m.id}`}>
                        {m.sent_emails.map((e, i) => (
                          <div key={i} className={`flex flex-wrap gap-1 ${e.status === "sent" ? "text-emerald-700" : "text-red-600"}`}>
                            <span className="font-bold">{e.status === "sent" ? "✔ تم الإرسال" : "✖ لم يُرسل"}</span>
                            <span>— {e.company_name || e.to_email}</span><span dir="ltr">({e.to_email})</span>
                            {e.in_thread && e.status === "sent" && <span data-testid="sourcing-sent-in-thread">· ضمن نفس المحادثة البريدية</span>}
                            {e.subject && <span>· {e.subject}</span>}{e.error && <span>· {e.error}</span>}
                          </div>
                        ))}
                      </div>
                    )}
                    {!mine && m.drafts?.length > 0 && (
                      <div className="mt-2 pt-2 border-t border-[#7AB2B2]/40 text-xs space-y-2" data-testid={`sourcing-drafts-${m.id}`}>
                        {m.drafts.map((d) => (
                          <div key={d.id} className="rounded-lg border border-[#7AB2B2]/60 bg-white p-2 space-y-1" data-testid={`sourcing-draft-${d.id}`}>
                            <div className="font-bold text-[#09637E]">
                              {d.status === "pending" ? "📝 مسودة بانتظار تأكيدك" : d.status === "sent" ? "✔ أُرسلت" : d.status === "failed" ? "✖ لم تُجهَّز" : "⏹ مسودة قديمة (لم تُرسل)"}
                              {d.in_thread && " · رد داخل نفس المحادثة البريدية"}
                            </div>
                            <div>إلى: <span className="font-bold">{d.company_name || "—"}</span> <span dir="ltr" data-testid="sourcing-draft-recipient">({d.to_email})</span></div>
                            {d.verification_ar && <div data-testid="sourcing-draft-verification">{d.verification_ar}</div>}
                            {d.error && <div className="text-red-600">{d.error}</div>}
                            {d.subject && <div>الموضوع: {d.subject}</div>}
                            {d.body && <div className="whitespace-pre-wrap break-words border-t border-[#7AB2B2]/30 pt-1" dir="auto">{d.body}</div>}
                            {d.status === "pending" && <div className="text-[#666666]">للإرسال اكتب مثلاً: «إي ابعت» — وللإلغاء: «لا تبعت».</div>}
                          </div>
                        ))}
                      </div>
                    )}
                    {!mine && m.sources?.length > 0 && (
                      <div className="mt-2 pt-2 border-t border-[#7AB2B2]/40 text-xs space-y-1" data-testid={`sourcing-sources-${m.id}`}>
                        <div className="font-bold text-[#09637E]">المصادر:</div>
                        {m.sources.slice(0, 8).map((s, i) => (
                          <a key={s.url} href={s.url} target="_blank" rel="noopener noreferrer" dir="ltr"
                            className="block truncate text-[#088395] hover:underline" title={s.url}>
                            {i + 1}. {s.title || s.url}
                          </a>
                        ))}
                      </div>
                    )}
                    <div className="text-[10px] opacity-70 mt-1">{fmt(m.ts)}</div>
                  </div>
                </div>
              );
            })}
          {sending && (
            <div className="flex justify-end" data-testid="sourcing-typing">
              <div className="bg-[#EBF4F6] text-[#666666] rounded-2xl px-4 py-2 text-sm animate-pulse">وكيل التوريد يكتب...</div>
            </div>
          )}
          <div ref={endRef} />
        </div>
        <form onSubmit={send} className="border-t border-[#7AB2B2] p-3 flex gap-2 items-center">
          <VoiceRecorder disabled={sending} onTranscript={(t) => setInput((cur) => (cur.trim() ? `${cur.trim()} ${t}` : t))} />
          <input value={input} onChange={(e) => setInput(e.target.value)} data-testid="sourcing-input" maxLength={4000}
            placeholder="اكتب طلبك أو سجّل صوتك..." disabled={sending}
            className="h-12 flex-1 min-w-0 rounded-lg border border-[#7AB2B2] px-4 focus:outline-none focus:ring-2 focus:ring-[#09637E] disabled:bg-gray-50" />
          <button type="submit" disabled={sending || !input.trim()} data-testid="sourcing-send"
            className="h-12 px-4 md:px-5 rounded-lg bg-[#09637E] hover:bg-[#088395] text-white font-bold flex items-center gap-2 disabled:opacity-50">
            <Send size={18} /> <span className="hidden md:inline">إرسال</span>
          </button>
        </form>
      </div>
    </div>
  );
};

export default ExternalSourcing;
